import { Trash2, Boxes } from 'lucide-react';
import type { IssuedKitItem, Station } from '@hcc/types';
import { fieldsFor } from '../../lib/kitItemFields';
import { AdditionalInfoEditor } from './AdditionalInfoEditor';

interface KitItemRowProps {
  item: IssuedKitItem;
  station: Station;
  /** Asset category when the row is a tracked asset, for field labels. */
  category: string | null;
  onPatch: (patch: Partial<IssuedKitItem>) => void;
  onRemove: () => void;
}

/**
 * One editable row in the kit-issue drawer: item name, quantity, and the
 * category-appropriate identifier / size fields (Serial vs Registration vs
 * Size per station and asset category, via fieldsFor).
 */
export function KitItemRow({ item, station, category, onPatch, onRemove }: KitItemRowProps) {
  const tracked = Boolean(item.asset_id);
  const fields = fieldsFor(station, category);
  const idIsRegistration = fields.identifier.label.toLowerCase().includes('registration');
  const idValue = (idIsRegistration ? item.registration : item.serial) ?? '';
  const setIdentifier = (v: string) => {
    if (idIsRegistration) {
      onPatch({ registration: v, serial: undefined });
    } else {
      onPatch({ serial: v, registration: undefined });
    }
  };

  return (
    <div
      className={`rounded-token-sm border p-3 ${
        tracked ? 'border-success/25 bg-success-soft/10' : 'border-line/15 bg-surface-2'
      }`}
    >
      {/* Top row: name + tracked badge + remove */}
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={item.name}
          onChange={(e) => onPatch({ name: e.target.value })}
          placeholder="Item name"
          readOnly={tracked}
          aria-label="Item name"
          className={`h-9 min-w-0 flex-1 rounded-token-sm border px-2 text-sm shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30 ${
            tracked
              ? 'border-success/25 bg-success-soft/10 text-success-soft-fg'
              : 'border-line-strong/20 bg-surface text-fg'
          }`}
        />
        {tracked ? (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-success-fg"
            title="Tracked asset — status will flip to issued on signing"
          >
            <Boxes className="h-3 w-3" />
            Tracked
          </span>
        ) : null}
        {category ? (
          <span className="hidden shrink-0 rounded-full bg-surface px-2 py-1 text-[10px] font-medium text-fg-muted ring-1 ring-line/15 sm:inline">
            {category}
          </span>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${item.name || 'item'}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-token-sm text-fg-muted transition-colors hover:bg-danger-soft/15 hover:text-danger"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Bottom row: qty + category-appropriate fields */}
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-12">
        <FieldCell label="Qty" className="sm:col-span-2">
          <input
            type="number"
            min={1}
            max={99}
            value={item.quantity}
            onChange={(e) => onPatch({ quantity: Number(e.target.value) || 1 })}
            disabled={tracked}
            aria-label="Quantity"
            className="h-9 w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:bg-surface-2 disabled:text-fg-muted"
          />
        </FieldCell>
        {fields.identifier.show ? (
          <FieldCell label={fields.identifier.label} className="sm:col-span-6">
            <input
              type="text"
              value={idValue}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={fields.identifier.placeholder}
              aria-label={fields.identifier.label}
              className="h-9 w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </FieldCell>
        ) : null}
        {fields.size.show ? (
          <FieldCell
            label={fields.size.label}
            className={fields.identifier.show ? 'sm:col-span-4' : 'sm:col-span-10'}
          >
            <input
              type="text"
              value={item.size ?? ''}
              onChange={(e) => onPatch({ size: e.target.value })}
              placeholder={fields.size.placeholder}
              aria-label={fields.size.label}
              className="h-9 w-full rounded-token-sm border border-line-strong/20 bg-surface px-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </FieldCell>
        ) : null}
      </div>

      {/* Extra attributes (Make, Model, ...) on any row, tracked or not: a
          manually added item is often exactly the one nothing is known about,
          so the desk needs somewhere to record what it is. On a tracked asset
          these also merge into the asset record for the CAFM export; on a
          manual row they live on the signed event. Optional and tucked behind
          a button so it never slows a normal issue down. */}
      <div className="mt-2 border-t border-divider/10 pt-2">
        <AdditionalInfoEditor
          value={item.attributes}
          onChange={(attrs) => onPatch({ attributes: attrs })}
        />
      </div>
    </div>
  );
}

function FieldCell({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-fg-dim">
        {label}
      </span>
      {children}
    </label>
  );
}
